import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  CheckCircle, XCircle, ChevronDown, ChevronUp,
  Activity, Clock, AlertTriangle, BarChart2
} from 'lucide-react';
import api from '../services/api';

// ─── Helpers ────────────────────────────────────────────────────────────────

function extractNames(log, projectMap) {
  const projectName =
    projectMap[log.pipelineId] ||
    (log.pipelineName?.includes(' - ')
      ? log.pipelineName.split(' - ')[0]
      : 'Unnamed Project');

  const pipelineName = log.pipelineName?.includes(' - ')
    ? log.pipelineName.split(' - ')[1]
    : log.pipelineName || 'Unnamed Pipeline';

  return { projectName, pipelineName };
}

function hasError(log) {
  return log.operations?.some(op => op.status === 'error');
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const ClassificationTable = ({ report }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-100 text-xs">
      <thead>
        <tr className="text-left text-gray-400 uppercase tracking-wider">
          <th className="px-3 py-2 font-medium">Class</th>
          <th className="px-3 py-2 font-medium">Precision</th>
          <th className="px-3 py-2 font-medium">Recall</th>
          <th className="px-3 py-2 font-medium">F1-Score</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-50">
        {Object.entries(report).map(([cls, metrics]) => {
          if (cls === 'accuracy') {
            return (
              <tr key="accuracy" className="bg-blue-50">
                <td className="px-3 py-2 font-medium text-gray-800">Accuracy</td>
                <td colSpan={3} className="px-3 py-2 text-blue-600 font-semibold">
                  {typeof metrics === 'number' ? `${(metrics * 100).toFixed(2)}%` : String(metrics)}
                </td>
              </tr>
            );
          }
          if (metrics && typeof metrics === 'object' && !['macro avg', 'weighted avg'].includes(cls)) {
            return (
              <tr key={cls} className="text-gray-600">
                <td className="px-3 py-2 font-medium text-gray-800">{cls}</td>
                <td className="px-3 py-2">{metrics.precision != null ? `${(metrics.precision * 100).toFixed(2)}%` : '—'}</td>
                <td className="px-3 py-2">{metrics.recall != null ? `${(metrics.recall * 100).toFixed(2)}%` : '—'}</td>
                <td className="px-3 py-2">{metrics['f1-score'] != null ? `${(metrics['f1-score'] * 100).toFixed(2)}%` : '—'}</td>
              </tr>
            );
          }
          return null;
        })}
      </tbody>
    </table>
  </div>
);

const SkeletonLog = () => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 animate-pulse space-y-3">
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-full bg-gray-100" />
      <div className="flex-1 space-y-1.5">
        <div className="h-3.5 bg-gray-200 rounded w-1/3" />
        <div className="h-3 bg-gray-100 rounded w-1/4" />
      </div>
    </div>
  </div>
);

const EmptyState = ({ onReset, filtered }) => (
  <div className="flex flex-col items-center justify-center py-20 text-center">
    <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
      <Activity className="w-7 h-7 text-gray-300" />
    </div>
    <h3 className="text-base font-semibold text-gray-700 mb-1">No executions found</h3>
    <p className="text-sm text-gray-400 mb-4 max-w-xs">
      {filtered
        ? 'No runs match the selected filter.'
        : 'Run a pipeline in the editor to see execution logs here.'}
    </p>
    {filtered && (
      <button
        onClick={onReset}
        className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm text-gray-600 transition-colors"
      >
        Show all runs
      </button>
    )}
  </div>
);

// ─── Main page ────────────────────────────────────────────────────────────────

const Analytics = () => {
  const [logs, setLogs]               = useState([]);
  const [projectMap, setProjectMap]   = useState({});
  const [loading, setLoading]         = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [filter, setFilter]           = useState('all');

  useEffect(() => {
    Promise.all([
      api.get('/logs'),
      api.get('/projects'),
    ])
      .then(([logsRes, projectsRes]) => {
        setLogs(logsRes.data);

        const mapping = {};
        projectsRes.data.forEach(project => {
          project.pipelines?.forEach(pipelineId => {
            mapping[pipelineId] = project.name;
          });
        });
        setProjectMap(mapping);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Annotate logs with extracted names
  const annotated = logs.map(log => ({
    ...log,
    ...extractNames(log, projectMap),
  }));

  const successCount = annotated.filter(l => !hasError(l)).length;
  const failedCount  = annotated.filter(l => hasError(l)).length;
  const successRate  = annotated.length
    ? Math.round((successCount / annotated.length) * 100)
    : null;

  const tabCounts = {
    all:     annotated.length,
    success: successCount,
    error:   failedCount,
  };

  const filtered = annotated.filter(log => {
    if (filter === 'all')     return true;
    if (filter === 'success') return !hasError(log);
    if (filter === 'error')   return hasError(log);
    return true;
  });

  const logsByProject = filtered.reduce((acc, log) => {
    const key = log.projectName;
    if (!acc[key]) acc[key] = [];
    acc[key].push(log);
    return acc;
  }, {});

  const latestRun = annotated[0]?.timestamp;

  const TABS = [
    { key: 'all',     label: 'All runs' },
    { key: 'success', label: 'Successful' },
    { key: 'error',   label: 'Failed' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-b from-white to-blue-50 border-b border-gray-100 px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <BarChart2 className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          </div>
          <p className="text-sm text-gray-500 mb-6 ml-10">
            {annotated.length === 0
              ? 'No pipeline executions yet'
              : `${annotated.length} execution${annotated.length !== 1 ? 's' : ''} across all pipelines`}
          </p>

          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-1.5 text-sm shadow-sm">
              <Activity className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-gray-500">Total runs</span>
              <span className="font-semibold text-gray-900">{annotated.length}</span>
            </div>
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-1.5 text-sm shadow-sm">
              <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-gray-500">Success rate</span>
              <span className="font-semibold text-emerald-600">
                {successRate != null ? `${successRate}%` : '—'}
              </span>
            </div>
            <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-1.5 text-sm shadow-sm">
              <Clock className="w-3.5 h-3.5 text-gray-400" />
              <span className="text-gray-500">Latest run</span>
              <span className="font-semibold text-gray-900">
                {latestRun ? format(new Date(latestRun), 'MMM d, yyyy') : '—'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="bg-white border-b border-gray-100 px-6">
        <div className="max-w-5xl mx-auto flex gap-1">
          {TABS.map(tab => {
            const active = tab.key === filter;
            return (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-3 text-sm font-medium border-b-2 transition-colors duration-150 ${
                  active
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200'
                }`}
              >
                {tab.label}
                <span className={`text-xs rounded-full px-1.5 py-0.5 font-medium ${
                  active ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
                }`}>
                  {tabCounts[tab.key]}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-8">
        {loading ? (
          <div className="space-y-3">
            <SkeletonLog />
            <SkeletonLog />
            <SkeletonLog />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState onReset={() => setFilter('all')} filtered={filter !== 'all'} />
        ) : (
          <div className="space-y-8">
            {Object.entries(logsByProject).map(([projectName, projectLogs]) => (
              <div key={projectName}>
                {/* Project group header */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  <h2 className="text-sm font-semibold text-gray-700">{projectName}</h2>
                  <span className="text-xs text-gray-400">
                    {projectLogs.length} run{projectLogs.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className="space-y-2">
                  {projectLogs.map(log => {
                    const failed   = hasError(log);
                    const expanded = selectedLog === log._id;
                    return (
                      <div
                        key={log._id}
                        className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden"
                      >
                        {/* Row */}
                        <div
                          className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => setSelectedLog(expanded ? null : log._id)}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            failed ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'
                          }`}>
                            {failed
                              ? <AlertTriangle className="w-4 h-4" />
                              : <CheckCircle className="w-4 h-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{log.pipelineName}</p>
                            <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                              <Clock className="w-3 h-3" />
                              {format(new Date(log.timestamp), 'MMM d, yyyy · HH:mm:ss')}
                            </p>
                          </div>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${
                            failed ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {failed ? 'Failed' : 'Success'}
                          </span>
                          {expanded
                            ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                        </div>

                        {/* Expanded detail */}
                        {expanded && (
                          <div className="border-t border-gray-100 divide-y divide-gray-100">
                            {/* Operations */}
                            <div className="p-4">
                              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Operations</p>
                              <div className="space-y-3">
                                {log.operations?.map((op, i) => (
                                  <div key={i} className="flex gap-3">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                      op.status === 'completed'
                                        ? 'bg-emerald-100 text-emerald-600'
                                        : 'bg-red-100 text-red-600'
                                    }`}>
                                      {op.status === 'completed'
                                        ? <CheckCircle className="w-3.5 h-3.5" />
                                        : <XCircle className="w-3.5 h-3.5" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex justify-between items-baseline mb-1">
                                        <span className="text-sm font-medium text-gray-800">{op.type}</span>
                                        <span className="text-xs text-gray-400 flex-shrink-0">
                                          {format(new Date(op.timestamp), 'HH:mm:ss')}
                                        </span>
                                      </div>
                                      {op.logs?.length > 0 && (
                                        <div className="bg-gray-50 border border-gray-100 rounded-lg p-2 max-h-24 overflow-y-auto">
                                          {op.logs.map((entry, j) => (
                                            <div key={j} className="text-xs font-mono text-gray-500 py-0.5">{entry}</div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Metrics */}
                            {log.metrics && Object.keys(log.metrics).length > 0 && (
                              <div className="p-4">
                                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Performance Metrics</p>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                  {Object.entries(log.metrics).map(([key, value]) => {
                                    if (key === 'classification_report') {
                                      return (
                                        <div key={key} className="col-span-full bg-gray-50 rounded-lg border border-gray-100 p-3">
                                          <p className="text-xs text-gray-500 mb-2 font-medium">Classification Report</p>
                                          <ClassificationTable report={value} />
                                        </div>
                                      );
                                    }
                                    if (typeof value !== 'number') return null;
                                    return (
                                      <div key={key} className="bg-gray-50 rounded-lg border border-gray-100 p-3">
                                        <p className="text-xs text-gray-500 mb-1">{key.replace(/_/g, ' ')}</p>
                                        <p className="text-lg font-bold text-blue-600">
                                          {value <= 1 && value >= 0 ? `${(value * 100).toFixed(2)}%` : value.toFixed(4)}
                                        </p>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Visualizations */}
                            {log.visualizations?.plots && Object.keys(log.visualizations.plots).length > 0 && (
                              <div className="p-4">
                                <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Visualizations</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                  {Object.entries(log.visualizations.plots).map(([key, plot]) => (
                                    <div key={key} className="bg-gray-50 rounded-lg border border-gray-100 p-3">
                                      <p className="text-xs text-gray-500 mb-2">{key.replace(/_/g, ' ')}</p>
                                      <img
                                        src={`data:image/png;base64,${plot}`}
                                        alt={key}
                                        className="rounded-md max-h-56 w-full object-contain"
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
